import ResizableDrawerNavigationBar from "@components/ResizableDrawer/ResizableDrawerNavigationBar"
import ResizeControl from "@components/ResizableDrawer/ResizeControl"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { shortcuts } from "@features/keymap/shortcuts"
import useHTMLElementSize from "@hooks/useHTMLElementSize"
import { useThrottle } from "@hooks/useThrottle"
import { Drawer, DrawerProps, Grid, SxProps, Theme } from "@mui/material"
import { ReactNode, useCallback, useEffect } from "react"
import { useLocalStorage } from "usehooks-ts"

const DEFAULT_WIDTH = 250
const MIN_WIDTH = 250

const DEFAULT_HEIGHT = 200
const MIN_HEIGHT = 200

export type Anchor = "bottom" | "left" | "right"

export type ResizableDrawerProps = {
  showBackdrop?: boolean
  anchor: Anchor
  onAnchorChange?: (newAnchor: Anchor) => void
  onClose?: () => void
  localStoragePrefix: string
  menuComponent?: ReactNode
  navigationBarTitle?: ReactNode
  navigationBarSx?: SxProps<Theme>
  enableMaximising?: boolean
  defaultWidth?: number
  keepMounted?: boolean
  resizeControlPosition?: "end" | "start"
} & DrawerProps

const ResizableDrawer = ({
  children,
  anchor,
  showBackdrop,
  localStoragePrefix,
  onClose,
  onAnchorChange,
  menuComponent,
  navigationBarTitle,
  navigationBarSx,
  enableMaximising = true,
  defaultWidth = DEFAULT_WIDTH,
  keepMounted,
  resizeControlPosition = "start",
  PaperProps,
  ModalProps,
  ...props
}: ResizableDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useLocalStorage(
    `${localStoragePrefix}-node-drawer-width`,
    defaultWidth,
  )
  const [drawerHeight, setDrawerHeight] = useLocalStorage(
    `${localStoragePrefix}-node-drawer-height`,
    DEFAULT_HEIGHT,
  )
  const [isMaximised, setIsMaximised] = useLocalStorage(`${localStoragePrefix}-is-maximised`, false)

  const { width: viewPortWidth, height: viewPortHeight } = useHTMLElementSize(document.body)

  // adjust drawer size if user makes browser window smaller
  useEffect(() => {
    if (!viewPortHeight || !viewPortWidth) return

    const isDrawerTooWide = viewPortWidth < drawerWidth
    const isDrawerTooHigh = viewPortHeight < drawerHeight

    if (anchor !== "bottom" && isDrawerTooWide) setDrawerWidth(viewPortWidth)
    if (anchor === "bottom" && isDrawerTooHigh) setDrawerHeight(viewPortHeight)
  }, [
    anchor,
    viewPortWidth,
    viewPortHeight,
    drawerHeight,
    drawerWidth,
    setDrawerHeight,
    setDrawerWidth,
  ])

  const resizeVertically = useCallback(
    (event: MouseEvent) => {
      const newWidth =
        anchor === "right" ? document.body.offsetWidth - event.clientX : event.clientX
      const maxWidth = document.body.clientWidth

      if (MIN_WIDTH > newWidth) return
      if (maxWidth <= newWidth) return

      setDrawerWidth(newWidth)
    },
    [anchor, setDrawerWidth],
  )

  const resizeHorizontally = useCallback(
    (event: MouseEvent) => {
      const newHeight = document.body.offsetHeight - event.clientY
      const maxHeight = document.body.clientHeight - 30

      if (MIN_HEIGHT > newHeight) return
      if (maxHeight <= newHeight) return

      setDrawerHeight(newHeight)
    },
    [setDrawerHeight],
  )

  const handleResize = useThrottle((event: MouseEvent) => {
    setIsMaximised(false)

    if (anchor !== "bottom") resizeVertically(event)
    else resizeHorizontally(event)

    const selection = document.getSelection()
    if (selection) selection.removeAllRanges()
  }, 50)

  const handleRestoreSize = () => {
    setIsMaximised(false)

    // prevent from going back to size that is bigger than current view port size
    if (anchor !== "bottom" && drawerWidth > viewPortWidth) setDrawerWidth(viewPortWidth / 2)
    if (anchor === "bottom" && drawerHeight > viewPortHeight) setDrawerHeight(viewPortHeight / 2)
  }

  const resizeControl = (
    <Grid item xs="auto" sx={{ height: anchor !== "bottom" ? "100%" : undefined }}>
      <ResizeControl
        direction={anchor !== "bottom" ? "vertical" : "horizontal"}
        onResize={handleResize}
      />
    </Grid>
  )

  return (
    <Drawer
      disableEscapeKeyDown // ESC is managed my KeyBinding component
      PaperProps={{
        sx: {
          ...(anchor !== "bottom" && {
            width: isMaximised ? viewPortWidth : drawerWidth,
            ...(props.variant === "permanent"
              ? {
                  marginTop: "75px",
                  marginBottom: "15px",
                }
              : {
                  top: "75px",
                  bottom: "15px",
                  height: "auto",
                }),
            ...(anchor === "right" ? { marginRight: "10px" } : { marginLeft: "10px" }),
            borderRadius: "3px !important",
            outline: "1px solid #ccc",
          }),
          ...(anchor === "bottom" && {
            height: isMaximised ? viewPortHeight - 60 : drawerHeight,
            margin: "20px",
            borderRadius: "3px !important",
            outline: "1px solid #ccc",
            paddingLeft: "1em",
            paddingRight: "1em",
          }),
          ...PaperProps?.sx,
        },
      }}
      sx={{
        flexShrink: 0,
        ...(props.variant === "permanent" && {
          width: drawerWidth,
          "& .MuiDrawer-paper": { width: drawerWidth },
        }),
        ...props.sx,
      }}
      anchor={anchor}
      // Allow to click outside drawer
      ModalProps={{
        ...(showBackdrop
          ? {
              slotProps: { root: { style: { backgroundColor: "rgba(0, 0, 0, 0.2)" } } },
            }
          : {
              slots: { backdrop: "div" },
              slotProps: {
                root: {
                  style: {
                    position: "absolute",
                    top: "unset",
                    bottom: "unset",
                    left: "unset",
                    right: "unset",
                  },
                },
              },
            }),
        ...ModalProps,
        ...(keepMounted !== undefined && { keepMounted }),
      }}
      // @TODO (Bartek Laskowski) 2025-06-11: decouple MUI props from component props to prevent leaking non-MUI props into MuiDrawer
      {...props}
    >
      <Grid
        container
        direction="column"
        sx={{ width: "100%", height: "100%", minHeight: 0 }}
        wrap="nowrap"
      >
        <Grid item xs="auto">
          <KeyBinding bind={shortcuts["close-drawer"]} onKeyDown={onClose} global />
          <ResizableDrawerNavigationBar
            anchor={anchor}
            isMaximised={isMaximised}
            onAnchorChange={onAnchorChange}
            onClose={onClose}
            onMaximise={enableMaximising ? () => setIsMaximised(true) : undefined}
            onRestoreSize={enableMaximising ? handleRestoreSize : undefined}
            menuComponent={menuComponent}
            title={navigationBarTitle}
            sx={navigationBarSx}
          />
        </Grid>

        <Grid
          container
          item
          xs
          direction={anchor === "bottom" ? "column" : "row"}
          wrap="nowrap"
          sx={{ minHeight: 0, overflow: "hidden" }}
        >
          {resizeControlPosition === "start" && resizeControl}

          <Grid item xs sx={{ minHeight: 0, overflow: "hidden" }}>
            {children}
          </Grid>

          {resizeControlPosition === "end" && resizeControl}
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default ResizableDrawer
